/**
 * CAI Control Tower — Executive portfolio view
 * /cai
 */
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import client from "@/api/client";
import { toast } from "sonner";
import { Plus, RefreshCw, AlertTriangle, Leaf, Target, TrendingUp, BarChart2, ChevronRight, ChevronDown, X, ArrowUpDown } from "lucide-react";

import { PageHeader, SectionCard, MetricCard, CTAButton } from "@/components/ds";
const STAGE_COLORS = {
  INTAKE:"bg-slate-400", SCREENING:"bg-blue-400", STRUCTURING:"bg-indigo-400",
  MRV_SETUP:"bg-violet-400", MRV_ACTIVE:"bg-purple-400", VERIFICATION:"bg-amber-400",
  CLASSIFICATION:"bg-orange-400", VALUE_READY:"bg-emerald-500",
  ARTICLE6_SUBMISSION:"bg-teal-500", ISSUED:"bg-emerald-600",
};
const PATHWAY_CONFIG = {
  article6_candidate:       { color:"text-teal-700 bg-teal-50 border-teal-200",    label:"Article 6",   icon:"🟢" },
  carbon_credit_candidate:  { color:"text-amber-700 bg-amber-50 border-amber-200", label:"Carbon Credit",icon:"🟡" },
  climate_performance_asset:{ color:"text-blue-700 bg-blue-50 border-blue-200",    label:"CPA",         icon:"🔵" },
  unclassified:             { color:"text-slate-500 bg-slate-100 border-slate-200", label:"Unclassified",icon:"⚪" },
};
const fmt = (n, d=1) => n != null && !isNaN(n) ? (+n).toFixed(d) : "—";

function StatCard({ label, value, accent, sub }) {
  return (
    <MetricCard label={label} value={value} sub={sub} color={accent ? "text-emerald-700" : "text-slate-900"} />
  );
}

function NewProjectModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ name:"", sector:"", project_type:"", country:"", geography:"" });
  const [saving, setSaving] = useState(false);
  const SECTORS = ["Energy","Waste Management & Recycling","Manufacturing","Agriculture","Transport & Logistics","Water & Utilities","Construction"];
  const TYPES   = ["Biogas","Solar PV","Wind","Energy Efficiency","Fuel Switch","Waste Reduction","Nature-Based","Other"];

  const handleCreate = async () => {
    if (!form.name) { toast.error("Project name required"); return; }
    setSaving(true);
    try {
      const res = await client.post("/cai/projects", form);
      toast.success(`Project ${res.data.project_id} created!`);
      onCreated(res.data.id);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-lg w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-bold text-slate-900">New Climate Asset Project</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-lg">✕</button>
        </div>
        <div className="space-y-3">
          <div><label className="text-xs font-medium text-slate-600 mb-1 block">Project Name *</label>
            <input value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} placeholder="Oko-Oba Biogas Project"
              className="w-full h-10 px-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-1 focus:ring-[#059669]" /></div>
          <div className="grid grid-cols-2 gap-4">
            {[["sector","Sector",SECTORS],["project_type","Project Type",TYPES]].map(([k,l,opts])=>(
              <div key={k}><label className="text-xs font-medium text-slate-600 mb-1 block">{l}</label>
                <select value={form[k]} onChange={e=>setForm(f=>({...f,[k]:e.target.value}))}
                  className="w-full h-10 px-3 border border-slate-200 rounded-xl text-sm bg-white focus:outline-none">
                  <option value="">Select...</option>
                  {opts.map(o=><option key={o} value={o}>{o}</option>)}
                </select></div>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[["country","Country","Nigeria"],["geography","ISO Code (2 letters)","NG"]].map(([k,l,p])=>(
              <div key={k}><label className="text-xs font-medium text-slate-600 mb-1 block">{l}</label>
                <input value={form[k]} onChange={e=>setForm(f=>({...f,[k]:e.target.value}))} placeholder={p}
                  className="w-full h-10 px-3 border border-slate-200 rounded-xl text-sm focus:outline-none" /></div>
            ))}
          </div>
        </div>
        <div className="flex gap-2 mt-4">
          <button onClick={handleCreate} disabled={saving}
            className="flex-1 h-10 bg-[#059669] text-white text-sm font-medium rounded-xl hover:bg-emerald-700 disabled:opacity-50">
            {saving ? "Creating..." : "Create Project"}
          </button>
          <button onClick={onClose} className="px-4 border border-slate-200 text-slate-600 text-sm rounded-xl hover:bg-slate-50">Cancel</button>
        </div>
      </div>
    </div>
  );
}

export default function CAIControlTower() {
  const navigate = useNavigate();
  const [stats,        setStats]       = useState(null);
  const [projects,     setProjects]    = useState([]);
  const [loading,      setLoading]     = useState(true);
  const [showNew,      setShowNew]     = useState(false);
  const [stageFilter,  setStageFilter] = useState(null);   // funnel chart click filter
  const [sortBy,       setSortBy]      = useState("cars"); // cars | name | stage | risk
  const [sortOpen,     setSortOpen]    = useState(false);  // sort dropdown open state

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sRes, pRes] = await Promise.all([
        client.get("/cai/projects/stats"),
        client.get("/cai/projects"),
      ]);
      setStats(sRes.data);
      setProjects(pRes.data?.projects || []);
    } catch { toast.error("Failed to load CAI data"); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Close sort dropdown when clicking outside
  useEffect(() => {
    if (!sortOpen) return;
    const handler = (e) => {
      if (!e.target.closest('[data-testid="sort-dropdown-trigger"]') &&
          !e.target.closest('[data-testid="sort-dropdown-menu"]')) {
        setSortOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [sortOpen]);

  const handleCreated = (id) => { setShowNew(false); navigate(`/cai/projects/${id}`); };

  // Apply stage filter and sort
  const SORT_OPTIONS = [
    { value: "cars",  label: "CARS Score" },
    { value: "name",  label: "Project Name" },
    { value: "stage", label: "Stage" },
    { value: "risk",  label: "Risk Level" },
  ];
  const STAGE_ORDER = ["INTAKE","SCREENING","STRUCTURING","MRV_SETUP","MRV_ACTIVE","VERIFICATION","CLASSIFICATION","VALUE_READY","ARTICLE6_SUBMISSION","ISSUED"];
  const RISK_ORDER  = { high: 0, medium: 1, low: 2 };

  const filteredProjects = projects
    .filter(p => !stageFilter || p.stage === stageFilter)
    .sort((a, b) => {
      switch (sortBy) {
        case "cars":  return (b.cars_score || 0) - (a.cars_score || 0);
        case "name":  return (a.name || "").localeCompare(b.name || "");
        case "stage": return STAGE_ORDER.indexOf(a.stage) - STAGE_ORDER.indexOf(b.stage);
        case "risk":  return (RISK_ORDER[a.risk?.level] ?? 9) - (RISK_ORDER[b.risk?.level] ?? 9);
        default: return 0;
      }
    });

  return (
    <div className="p-6 lg:p-8 min-h-screen bg-[#F8FAFC]" data-testid="cai-control-tower">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
              <Leaf size={16} className="text-[#059669]" />
            </div>
            
      <PageHeader
        title="Climate Asset Infrastructure"
        subtitle="Portfolio view — structuring, verification, and classification control tower."
      />
          <p className="text-sm text-slate-500">Portfolio view — structuring, verification, and classification control tower.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="p-2 border border-slate-200 rounded-xl text-slate-500 hover:bg-slate-100">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          </button>
          <button onClick={() => setShowNew(true)} data-testid="new-project-btn"
            className="flex items-center gap-1.5 px-4 py-2 bg-[#059669] text-white text-sm font-medium rounded-xl hover:bg-emerald-700">
            <Plus size={14} /> New Project
          </button>
        </div>
      </div>

      {/* KPI Strip */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
          <StatCard label="Total Projects" value={stats.total_projects} />
          <StatCard label="Active"         value={stats.active} accent />
          <StatCard label="Value Ready"    value={stats.value_ready} sub="stage ≥ VALUE_READY" />
          <StatCard label="CO₂ Reduction"  value={`${fmt(stats.total_reduction_estimate_tco2e,0)} t`} sub="estimated" />
          <StatCard label="Credit Potential" value={`${fmt(stats.credit_estimate_tco2e,0)} t`} sub="DPCS+ eligible" />
          <StatCard label="High Risk"      value={stats.high_risk_count} sub={stats.high_risk_count>0?"requires attention":"all clear"} />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-5">
        {/* Pipeline Funnel */}
        {stats?.pipeline_funnel && (
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                <BarChart2 size={14} className="text-[#059669]" /> Pipeline Funnel
              </h3>
              {stageFilter && (
                <button onClick={() => setStageFilter(null)}
                  className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-700 border border-slate-200 rounded-lg px-2 py-0.5"
                  data-testid="clear-stage-filter">
                  <X size={9} /> Clear filter
                </button>
              )}
            </div>
            <div className="space-y-2">
              {stats.pipeline_funnel.filter(s => s.count > 0 || ["INTAKE","VALUE_READY","ISSUED"].includes(s.stage)).map(s => {
                const isActive = stageFilter === s.stage;
                return (
                  <div key={s.stage}
                    className={`flex items-center gap-2 rounded-lg px-1.5 py-1 cursor-pointer transition-colors ${isActive ? "bg-emerald-50" : "hover:bg-slate-50"}`}
                    onClick={() => setStageFilter(stageFilter === s.stage ? null : s.stage)}
                    data-testid={`funnel-stage-${s.stage}`}>
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${STAGE_COLORS[s.stage] || "bg-slate-300"}`} />
                    <span className={`text-xs w-32 ${isActive ? "font-bold text-[#059669]" : "text-slate-500"}`}>
                      {s.stage.replace(/_/g," ")}
                    </span>
                    <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full transition-all ${isActive ? "bg-[#059669]" : STAGE_COLORS[s.stage] || "bg-slate-300"}`}
                        style={{width: stats.total_projects > 0 ? `${s.count/stats.total_projects*100}%` : "0%"}} />
                    </div>
                    <span className={`text-xs font-bold w-4 text-right ${isActive ? "text-[#059669]" : "text-slate-700"}`}>{s.count}</span>
                  </div>
                );
              })}
            </div>
            {stageFilter && (
              <p className="text-xs text-emerald-600 mt-2 font-semibold">
                Filtered by stage: {stageFilter.replace(/_/g," ")}
              </p>
            )}
          </div>
        )}

        {/* Pathway Distribution */}
        {stats?.by_pathway && (
          <SectionCard>

            <h3 className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Target size={14} className="text-[#059669]" /> Value Pathway Split
            </h3>
            <div className="space-y-3">
              {Object.entries(stats.by_pathway).map(([pathway, count]) => {
                const cfg = PATHWAY_CONFIG[pathway] || PATHWAY_CONFIG.unclassified;
                return (
                  <div key={pathway} className="flex items-center justify-between">
                    <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${cfg.color}`}>{cfg.label}</span>
                    <div className="flex items-center gap-2">
                      <div className="w-20 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full bg-[#059669]"
                          style={{width: stats.total_projects > 0 ? `${count/stats.total_projects*100}%` : "0%"}} />
                      </div>
                      <span className="text-sm font-semibold text-slate-900 tabular-nums">{count}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </SectionCard>
        )}

        {/* Risk Distribution */}
        {stats?.by_risk && (
          <SectionCard>

            <h3 className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <AlertTriangle size={14} className="text-amber-500" /> Risk Distribution
            </h3>
            <div className="space-y-3">
              {[["high","High Risk","bg-red-500 text-red-700 bg-red-50"],["medium","Medium Risk","bg-amber-500 text-amber-700 bg-amber-50"],["low","Low Risk","bg-emerald-500 text-emerald-700 bg-emerald-50"]].map(([k,l,colors])=>(
                <div key={k} className="flex items-center justify-between">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${colors.split(" ").slice(1).join(" ")}`}>{l}</span>
                  <span className="text-lg font-bold text-slate-900 tabular-nums">{stats.by_risk?.[k] || 0}</span>
                </div>
              ))}
            </div>
          </SectionCard>
        )}
      </div>

      {/* Project Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="project-list">
        {/* Table header with filter info + sort dropdown */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50">
          <div className="flex items-center gap-2">
            <p className="text-xs font-semibold text-slate-500">
              {stageFilter
                ? `${filteredProjects.length} project(s) in ${stageFilter.replace(/_/g," ")}`
                : `${filteredProjects.length} project(s)`}
            </p>
            {stageFilter && (
              <button onClick={() => setStageFilter(null)} data-testid="table-clear-filter"
                className="flex items-center gap-1 text-xs text-emerald-600 hover:text-emerald-800">
                <X size={10} /> Clear
              </button>
            )}
          </div>
          {/* Sort dropdown */}
          <div className="relative">
            <button onClick={() => setSortOpen(o => !o)} data-testid="sort-dropdown-trigger"
              className="flex items-center gap-1.5 px-3 h-7 border border-slate-200 rounded-lg text-xs text-slate-500 hover:bg-slate-100 bg-white">
              <ArrowUpDown size={11} />
              Sort: {SORT_OPTIONS.find(o => o.value === sortBy)?.label}
              <ChevronDown size={10} />
            </button>
            {sortOpen && (
              <div className="absolute right-0 top-8 z-20 bg-white border border-slate-200 rounded-xl shadow-lg py-1 min-w-36"
                data-testid="sort-dropdown-menu">
                {SORT_OPTIONS.map(opt => (
                  <button key={opt.value}
                    onClick={() => { setSortBy(opt.value); setSortOpen(false); }}
                    data-testid={`sort-option-${opt.value}`}
                    className={`w-full text-left px-4 py-2 text-xs hover:bg-slate-50 flex items-center gap-2 ${sortBy === opt.value ? "text-[#059669] font-semibold" : "text-slate-600"}`}>
                    {sortBy === opt.value && <span className="text-[#059669]">✓</span>}
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[700px]">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                {["Project","Sector","Country","Stage","CARS","Pathway","Risk","Action"].map(h=>(
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? [...Array(3)].map((_,i)=>(
                <tr key={i}>{[...Array(8)].map((_,j)=><td key={j} className="px-4 py-3"><div className="h-4 bg-slate-100 rounded animate-pulse"/></td>)}</tr>
              )) : filteredProjects.length===0 ? (
                <tr><td colSpan={8} className="px-4 py-16 text-center text-slate-400">
                  <Leaf size={28} className="mx-auto mb-2 opacity-30"/>
                  <p className="text-sm">
                    {stageFilter ? `No projects at stage "${stageFilter.replace(/_/g," ")}"` : "No projects yet. Create your first climate asset project."}
                  </p>
                  {stageFilter && <button onClick={() => setStageFilter(null)} className="mt-2 text-xs text-[#059669] underline">Clear filter</button>}
                </td></tr>
              ) : filteredProjects.map(p=>{
                const pathwayCfg = PATHWAY_CONFIG[p.classification?.value_pathway] || PATHWAY_CONFIG.unclassified;
                return (
                  <tr key={p.id} data-testid={`project-row-${p.project_id}`}
                    className="hover:bg-slate-50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/cai/projects/${p.id}`)}>
                    <td className="px-4 py-3">
                      <p className="text-sm font-semibold text-slate-900">{p.name}</p>
                      <p className="text-xs text-slate-400 font-mono">{p.project_id}</p>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">{p.sector||"—"}</td>
                    <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">{p.country||"—"}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="flex items-center gap-1.5 text-xs font-medium">
                        <div className={`w-2 h-2 rounded-full ${STAGE_COLORS[p.stage]||"bg-slate-400"}`} />
                        {p.stage}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm font-bold text-slate-700 tabular-nums whitespace-nowrap">
                      {p.cars_score!=null ? `${fmt(p.cars_score,0)}/100` : "—"}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${pathwayCfg.color}`}>
                        {pathwayCfg.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${p.risk?.level==="high"?"bg-red-50 text-red-700":p.risk?.level==="medium"?"bg-amber-50 text-amber-700":"bg-emerald-50 text-emerald-700"}`}>
                        {p.risk?.level||"—"}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <button className="flex items-center gap-1 text-xs text-[#059669] font-medium hover:text-emerald-700">
                        Open <ChevronRight size={12}/>
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {showNew && <NewProjectModal onClose={() => setShowNew(false)} onCreated={handleCreated} />}
    </div>
  );
}
